import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent } from '@ohif/ui-next';
import { ICON_STROKE_WIDTH } from '../../constants';
import FormField from '../../components/common/FormField';
import RequiredLabel from '../../components/common/RequiredLabel';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import { trimFormStringFields } from '../../utils/trimFormStringFields';
import {
  getDefaultMachineFormValues,
  mapFormValuesToUpsertPayload,
} from '../../utils/machineMapper';
import {
  getMachineFormFieldErrors,
  hasMachineFormErrors,
} from '../../validation/machineFormValidation';
import machineService from '../../services/machineService';
import type { Machine, MachineFormValues } from '../../types/machine.types';
import './MachineManagement.css';

interface MachineRegisterDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (machine: Machine) => void;
  modalityOptions: { value: string; label: string }[];
  existingMachines: Machine[];
}

const MachineRegisterDialog: React.FC<MachineRegisterDialogProps> = ({
  open,
  onClose,
  onSuccess,
  modalityOptions,
  existingMachines,
}) => {
  const [values, setValues] = useState<MachineFormValues>(getDefaultMachineFormValues());
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof MachineFormValues, string>>>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValues(getDefaultMachineFormValues());
      setFieldErrors({});
      setSubmitError(null);
    }
  }, [open]);

  const setField = useCallback((field: keyof MachineFormValues, value: string | boolean) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = async () => {
    const trimmed = trimFormStringFields(
      values as unknown as Record<string, unknown>
    ) as unknown as MachineFormValues;
    const errors = getMachineFormFieldErrors(trimmed, existingMachines);
    setFieldErrors(errors);
    if (hasMachineFormErrors(errors)) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = mapFormValuesToUpsertPayload(trimmed);
      const result = await machineService.createMachine(payload);
      if (result.success && result.data) {
        let machine = result.data;
        if (!trimmed.active) {
          const deactivateResult = await machineService.deactivateMachine(machine.id);
          if (deactivateResult.success && deactivateResult.data) {
            machine = deactivateResult.data;
          } else if (deactivateResult.success) {
            machine = { ...machine, active: false };
          }
        }

        const connectivityResult = await machineService.checkConnectivityByAeTitle(
          machine.aeTitle,
          machine
        );
        if (connectivityResult.success && connectivityResult.data) {
          machine = connectivityResult.data;
        }

        onSuccess(machine);
        onClose();
      } else {
        setSubmitError(result.error || 'Failed to register machine.');
      }
    } catch {
      setSubmitError('Failed to register machine.');
    } finally {
      setSubmitting(false);
    }
  };

  const modalitySelectOptions = useMemo(
    () => modalityOptions.map(o => ({ value: o.value, label: o.label })),
    [modalityOptions]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && onClose()}
    >
      <DialogContent
        className="machine-register-dialog-content"
        showCloseButton={false}
      >
        <header className="machine-register-dialog-header">
          <div className="machine-register-dialog-header-text">
            <h2 className="machine-register-dialog-title">Register machine</h2>
            <p className="machine-register-dialog-subtitle">
              Add a new imaging machine to the registry for PACS/RIS integration.
            </p>
          </div>
          <button
            type="button"
            className="machine-register-dialog-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X
              size={18}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
        </header>

        <div className="machine-register-dialog-body">
          <div className="machine-form-field">
            <RequiredLabel
              label="Machine name"
              required
              htmlFor="machine-name"
            />
            <FormField
              id="machine-name"
              label=""
              placeholder="e.g. CT scanner 1"
              value={values.machineName}
              onChange={e => setField('machineName', e.target.value)}
              error={fieldErrors.machineName}
            />
          </div>

          <div className="machine-form-row machine-form-row--modality">
            <div className="machine-form-field">
              <FormSelectVirtualized
                label="Modality type"
                required
                name="modalityType"
                options={modalitySelectOptions}
                value={values.modalityType}
                onChange={e => setField('modalityType', e.target.value)}
                error={fieldErrors.modalityType}
              />
            </div>
            <div className="machine-form-field">
              <RequiredLabel
                label="Location"
                required
                htmlFor="machine-location"
              />
              <FormField
                id="machine-location"
                label=""
                placeholder="e.g. Radiology · Ground floor"
                value={values.location}
                onChange={e => setField('location', e.target.value)}
                error={fieldErrors.location}
              />
            </div>
          </div>

          <div className="machine-form-field">
            <RequiredLabel
              label="AE title"
              required
              htmlFor="machine-ae-title"
            />
            <FormField
              id="machine-ae-title"
              label=""
              placeholder="e.g. CT_AE01"
              value={values.aeTitle}
              onChange={e => setField('aeTitle', e.target.value.toUpperCase())}
              error={fieldErrors.aeTitle}
            />
          </div>

          <div className="machine-form-row machine-form-row--ip">
            <div className="machine-form-field">
              <RequiredLabel
                label="IP address"
                required
                htmlFor="machine-ip"
              />
              <FormField
                id="machine-ip"
                label=""
                placeholder="e.g. 10.0.2.11"
                value={values.ipAddress}
                onChange={e => setField('ipAddress', e.target.value)}
                error={fieldErrors.ipAddress}
              />
            </div>
            <div className="machine-form-field machine-form-field--port">
              <RequiredLabel
                label="Port"
                required
                htmlFor="machine-port"
              />
              <FormField
                id="machine-port"
                label=""
                placeholder="104"
                value={values.port}
                onChange={e => setField('port', e.target.value)}
                error={fieldErrors.port}
              />
            </div>
          </div>

          <div className="machine-form-active-row">
            <ToggleSwitch
              id="machine-register-active"
              checked={values.active}
              onChange={checked => setField('active', checked)}
            />
            <label
              htmlFor="machine-register-active"
              className="machine-form-active-label"
            >
              Active
            </label>
          </div>

          {submitError ? <p className="machine-form-submit-error">{submitError}</p> : null}
        </div>

        <footer className="machine-register-dialog-footer">
          <button
            type="button"
            className="machine-dialog-btn machine-dialog-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="machine-dialog-btn machine-dialog-btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Registering…' : 'Register machine'}
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};

export default MachineRegisterDialog;
